/**
 * Inline visualization cards rendered by message-bubble.tsx in response to
 * specific tool outputs. Each card is small and self-contained: the tool
 * itself only returns a `{ kind, ... }` payload, and the card fetches the
 * live data via the same React Query hooks the dedicated pages use, so the
 * chat history never freezes a stale snapshot.
 */
import { useNavigate } from 'react-router';
import {
  UserCircleIcon,
  ClockIcon,
  CalendarBlankIcon,
  CheckCircleIcon,
  CircleIcon,
  CircleDashedIcon,
  ArrowSquareOutIcon,
  FileTextIcon,
  ArrowRightIcon,
  WarningIcon,
  PathIcon,
  ListChecksIcon,
} from '@phosphor-icons/react';
import { useGraphEntity, useGraphActivity } from '~/hooks/queries/use-graph';
import { useCrons, formatSchedule, type CronJob } from '~/hooks/queries/use-crons';
import { useFileDetail } from '~/hooks/queries/use-files';
import { ActivityFeed } from '~/features/memory/activity-feed';
import { Tip } from '~/components/ui/tip';
import { formatDateTime } from '~/lib/local-datetime';

// ───────────────────────── shared chrome ──────────────────────────────

function CardShell({
  icon,
  title,
  meta,
  onOpen,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  meta?: string;
  onOpen?: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="border border-border rounded-lg overflow-hidden bg-card">
      <div className="px-3 py-2 flex items-center gap-2 text-xs border-b border-border">
        <span className="text-muted-foreground shrink-0">{icon}</span>
        <span className="font-medium text-foreground truncate">{title}</span>
        {meta && <span className="ml-auto text-muted-foreground shrink-0">{meta}</span>}
        {onOpen && (
          <Tip label="Open full view">
            <button
              type="button"
              onClick={onOpen}
              className={`text-muted-foreground hover:text-foreground transition-colors cursor-pointer ${meta ? '' : 'ml-auto'}`}
              aria-label="Open full view"
            >
              <ArrowSquareOutIcon size={13} />
            </button>
          </Tip>
        )}
      </div>
      <div className="px-3 py-2 text-sm">{children}</div>
    </div>
  );
}

function ErrorRow({ message }: { message: string }) {
  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground px-3 py-2 border border-border rounded-lg">
      <WarningIcon size={14} />
      <span>{message}</span>
    </div>
  );
}

// ───────────────────────── entity_show ────────────────────────────────

export interface EntityShowOutput {
  kind?: string;
  entity_id?: string;
  name?: string;
  type?: string;
  error?: string;
}

export function EntityShowCard({ output }: { output: EntityShowOutput }) {
  const navigate = useNavigate();
  const { data, isLoading } = useGraphEntity(output.entity_id ?? null);

  if (output.error || !output.entity_id) {
    return <ErrorRow message={output.error ?? 'No entity.'} />;
  }

  const top = data?.attributes?.slice(0, 5) ?? [];
  const rels = data?.relationships?.slice(0, 4) ?? [];

  return (
    <CardShell
      icon={<UserCircleIcon size={14} />}
      title={data?.entity.name ?? output.name ?? output.entity_id}
      meta={data?.entity.type ?? output.type}
      onOpen={() => navigate(`/memory?entity=${encodeURIComponent(output.entity_id!)}`)}
    >
      {isLoading ? (
        <div className="text-xs text-muted-foreground">Loading…</div>
      ) : (
        <div className="flex flex-col gap-2">
          {top.length > 0 && (
            <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
              {top.map((a) => (
                <div key={`${a.category}.${a.key}`} className="contents">
                  <span className="text-muted-foreground">
                    {a.category}.{a.key}
                  </span>
                  <span className="text-foreground truncate">{a.value}</span>
                </div>
              ))}
            </div>
          )}
          {rels.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1 border-t border-border">
              {rels.map((r) => (
                <span
                  key={r.id}
                  className="text-[length:var(--text-2xs)] px-1.5 py-0.5 rounded bg-muted text-muted-foreground"
                >
                  {r.relation} → {r.source_id === output.entity_id ? r.target_id : r.source_id}
                </span>
              ))}
            </div>
          )}
          {top.length === 0 && rels.length === 0 && (
            <span className="text-xs text-muted-foreground">
              No attributes or relationships yet.
            </span>
          )}
        </div>
      )}
    </CardShell>
  );
}

// ───────────────────────── activity_show ──────────────────────────────

export function ActivityShowCard({ output }: { output: { kind?: string; limit?: number } }) {
  const navigate = useNavigate();
  return (
    <CardShell
      icon={<ClockIcon size={14} />}
      title="Recent activity"
      meta={output.limit ? `last ${output.limit}` : undefined}
      onOpen={() => navigate('/memory?tab=activity')}
    >
      <div className="max-h-[280px] overflow-y-auto -mx-1">
        <ActivityFeed />
      </div>
    </CardShell>
  );
}

// ───────────────────────── schedule_show ──────────────────────────────

function nextRunMs(job: CronJob): number {
  if (job.nextRunAt) return new Date(job.nextRunAt).getTime();
  return Number.POSITIVE_INFINITY;
}

export function ScheduleShowCard({ output }: { output: { kind?: string; limit?: number } }) {
  const navigate = useNavigate();
  const { items: jobs, isLoading } = useCrons();
  const limit = output.limit ?? 10;

  const upcoming = (jobs ?? [])
    .filter((j) => j.enabled)
    .slice()
    .sort((a, b) => nextRunMs(a) - nextRunMs(b))
    .slice(0, limit);

  return (
    <CardShell
      icon={<CalendarBlankIcon size={14} />}
      title="Upcoming schedule"
      meta={`${upcoming.length} job${upcoming.length === 1 ? '' : 's'}`}
      onOpen={() => navigate('/crons')}
    >
      {isLoading ? (
        <div className="text-xs text-muted-foreground">Loading…</div>
      ) : upcoming.length === 0 ? (
        <div className="text-xs text-muted-foreground">Nothing scheduled.</div>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {upcoming.map((j) => (
            <li
              key={j.id}
              className="flex items-center gap-2 text-xs hover:bg-muted rounded px-1 py-0.5 cursor-pointer"
              onClick={() => navigate(`/crons?id=${encodeURIComponent(j.id)}`)}
            >
              <span className="truncate flex-1 text-foreground">{j.name}</span>
              <span className="text-muted-foreground font-mono shrink-0">
                {formatSchedule(j.schedule)}
              </span>
              {j.nextRunAt && (
                <span className="text-muted-foreground/70 shrink-0">
                  {formatDateTime(j.nextRunAt, {
                    month: 'short',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </CardShell>
  );
}

// ───────────────────────── todo_show ──────────────────────────────────

type TodoStatus = 'pending' | 'in_progress' | 'completed';
interface Todo {
  id: string;
  content: string;
  status: TodoStatus;
}

export function TodoShowCard({
  output,
}: {
  output: { kind?: string; todos?: Todo[]; error?: string };
}) {
  if (output.error) return <ErrorRow message={output.error} />;
  const todos = output.todos ?? [];
  const done = todos.filter((t) => t.status === 'completed').length;

  return (
    <CardShell
      icon={<ListChecksIcon size={14} />}
      title="Todos"
      meta={`${done}/${todos.length} done`}
    >
      {todos.length === 0 ? (
        <div className="text-xs text-muted-foreground">No todos in this session.</div>
      ) : (
        <ul className="flex flex-col gap-1">
          {todos.map((t) => {
            const Icon =
              t.status === 'completed'
                ? CheckCircleIcon
                : t.status === 'in_progress'
                  ? CircleDashedIcon
                  : CircleIcon;
            return (
              <li
                key={t.id}
                className={`flex items-start gap-2 text-xs ${t.status === 'completed' ? 'text-muted-foreground line-through' : 'text-foreground'}`}
              >
                <Icon
                  size={14}
                  weight={t.status === 'completed' ? 'fill' : 'regular'}
                  className={`mt-0.5 shrink-0 ${t.status === 'in_progress' ? 'text-amber-500 animate-pulse' : t.status === 'completed' ? 'text-success' : 'text-muted-foreground'}`}
                />
                <span className="flex-1">{t.content}</span>
              </li>
            );
          })}
        </ul>
      )}
    </CardShell>
  );
}

// ───────────────────────── relationship_path ──────────────────────────

interface PathNode {
  id: string;
  name: string;
  type: string;
  relation_in: string | null;
}

export function RelationshipPathCard({
  output,
}: {
  output: { kind?: string; hops?: number; path?: PathNode[]; error?: string };
}) {
  const navigate = useNavigate();
  if (output.error || !output.path?.length) {
    return <ErrorRow message={output.error ?? 'No path.'} />;
  }
  return (
    <CardShell
      icon={<PathIcon size={14} />}
      title={`${output.path[0].name} → ${output.path[output.path.length - 1].name}`}
      meta={`${output.hops} hop${output.hops === 1 ? '' : 's'}`}
    >
      <div className="flex flex-wrap items-center gap-1.5">
        {output.path.map((n, i) => (
          <span key={n.id} className="flex items-center gap-1.5">
            {i > 0 && (
              <span className="flex items-center gap-0.5 text-[length:var(--text-2xs)] text-muted-foreground">
                <ArrowRightIcon size={10} />
                <span className="italic">{n.relation_in}</span>
                <ArrowRightIcon size={10} />
              </span>
            )}
            <Tip label={n.type}>
              <button
                type="button"
                onClick={() => navigate(`/memory?entity=${encodeURIComponent(n.id)}`)}
                className="text-xs px-1.5 py-0.5 rounded bg-muted hover:bg-muted/70 transition-colors cursor-pointer"
              >
                {n.name}
              </button>
            </Tip>
          </span>
        ))}
      </div>
    </CardShell>
  );
}

// ───────────────────────── file_show ──────────────────────────────────

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export interface FileShowOutput {
  kind?: string;
  file_id?: string;
  name?: string;
  content_type?: string;
  size_bytes?: number;
  updated_at?: string;
  error?: string;
}

export function FileShowCard({ output }: { output: FileShowOutput }) {
  const navigate = useNavigate();
  const { data, isLoading } = useFileDetail(output.file_id ?? null);

  if (output.error || !output.file_id) {
    return <ErrorRow message={output.error ?? 'File not found.'} />;
  }

  const preview = (data?.content ?? '').slice(0, 320);
  const truncated = (data?.content?.length ?? 0) > preview.length;

  return (
    <CardShell
      icon={<FileTextIcon size={14} />}
      title={data?.name ?? output.name ?? output.file_id}
      meta={
        output.size_bytes != null
          ? `${formatBytes(output.size_bytes)} · ${output.content_type ?? 'text'}`
          : undefined
      }
      onOpen={() => navigate(`/files?id=${encodeURIComponent(output.file_id!)}`)}
    >
      {isLoading ? (
        <div className="text-xs text-muted-foreground">Loading…</div>
      ) : (
        <pre className="text-xs text-muted-foreground whitespace-pre-wrap font-mono bg-muted/50 rounded p-2 max-h-[180px] overflow-y-auto">
          {preview || <span className="italic">Empty file.</span>}
          {truncated && <span className="text-muted-foreground/60"> …</span>}
        </pre>
      )}
    </CardShell>
  );
}
